import { useState } from 'react';
import type { CheckinDay, Organization, PasswordManagerKind } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';

interface Props {
  org: Organization;
  onSaved: () => void | Promise<void>;
  // Admin or billing admin: may switch a client who left block hours to
  // project-only and back.
  canManageBilling?: boolean;
}

const DAYS: CheckinDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// "3 months after the notice date" — calendar add with short-month clamping
// (e.g. Nov 30 + 3mo => Feb 28/29), matching the backend's addMonthsExact.
function addMonthsStr(dateStr: string, months: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return '';
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const lastDay = new Date(Date.UTC(y, mo - 1 + months + 1, 0)).getUTCDate();
  const dt = new Date(Date.UTC(y, mo - 1 + months, Math.min(d, lastDay)));
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

const NOTICE_PERIOD_MONTHS = 3;

export function OrgProfileCard({ org, onSaved, canManageBilling = false }: Props): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(org.name);
  const [domain, setDomain] = useState(org.domain ?? '');
  const [timezone, setTimezone] = useState(org.timezone);
  const [day, setDay] = useState<CheckinDay | ''>(org.checkinDayOfWeek ?? '');
  const [time, setTime] = useState(org.checkinTime?.slice(0, 5) ?? '');
  const [pocName, setPocName] = useState(org.mainPocName ?? '');
  const [pocEmail, setPocEmail] = useState(org.mainPocEmail ?? '');
  const [pocCell, setPocCell] = useState(org.mainPocCell ?? '');
  const [psa, setPsa] = useState(org.psaName ?? '');
  const [phone, setPhone] = useState(org.phone ?? '');
  const [email, setEmail] = useState(org.email ?? '');
  const [addressLine1, setAddressLine1] = useState(org.addressLine1 ?? '');
  const [addressLine2, setAddressLine2] = useState(org.addressLine2 ?? '');
  const [addressCity, setAddressCity] = useState(org.addressCity ?? '');
  const [addressRegion, setAddressRegion] = useState(org.addressRegion ?? '');
  const [addressPostalCode, setAddressPostalCode] = useState(org.addressPostalCode ?? '');
  const [addressCountry, setAddressCountry] = useState(org.addressCountry ?? '');
  const [rmm, setRmm] = useState(org.rmmName ?? '');
  const [pmKind, setPmKind] = useState<PasswordManagerKind | ''>(org.passwordManagerKind ?? '');
  const [pmOther, setPmOther] = useState(org.passwordManagerOtherName ?? '');
  const [startedOn, setStartedOn] = useState(org.startedOn ?? '');
  const [endedOn, setEndedOn] = useState(org.endedOn ?? '');
  const [noticeGivenOn, setNoticeGivenOn] = useState(org.noticeGivenOn ?? '');
  const [isActive, setIsActive] = useState(org.isActive);
  const [huduUrl, setHuduUrl] = useState(org.huduCompanyUrl ?? '');
  const [ticketingSystem, setTicketingSystem] = useState(org.ticketingSystem ?? '');
  const [appMailbox, setAppMailbox] = useState(org.appSharedMailboxEmail ?? '');

  function reset() {
    setName(org.name);
    setDomain(org.domain ?? '');
    setTimezone(org.timezone);
    setDay(org.checkinDayOfWeek ?? '');
    setTime(org.checkinTime?.slice(0, 5) ?? '');
    setPocName(org.mainPocName ?? '');
    setPocEmail(org.mainPocEmail ?? '');
    setPocCell(org.mainPocCell ?? '');
    setPsa(org.psaName ?? '');
    setPhone(org.phone ?? '');
    setEmail(org.email ?? '');
    setAddressLine1(org.addressLine1 ?? '');
    setAddressLine2(org.addressLine2 ?? '');
    setAddressCity(org.addressCity ?? '');
    setAddressRegion(org.addressRegion ?? '');
    setAddressPostalCode(org.addressPostalCode ?? '');
    setAddressCountry(org.addressCountry ?? '');
    setRmm(org.rmmName ?? '');
    setPmKind(org.passwordManagerKind ?? '');
    setPmOther(org.passwordManagerOtherName ?? '');
    setStartedOn(org.startedOn ?? '');
    setEndedOn(org.endedOn ?? '');
    setNoticeGivenOn(org.noticeGivenOn ?? '');
    setIsActive(org.isActive);
    setHuduUrl(org.huduCompanyUrl ?? '');
    setTicketingSystem(org.ticketingSystem ?? '');
    setAppMailbox(org.appSharedMailboxEmail ?? '');
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/organizations/${org.id}`, {
        method: 'PATCH',
        json: {
          name: name.trim() || org.name,
          domain: domain.trim() || null,
          timezone: timezone.trim() || org.timezone,
          checkinDayOfWeek: day || null,
          checkinTime: time || null,
          mainPocName: pocName.trim() || null,
          mainPocEmail: pocEmail.trim() || null,
          mainPocCell: pocCell.trim() || null,
          psaName: psa.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          addressLine1: addressLine1.trim() || null,
          addressLine2: addressLine2.trim() || null,
          addressCity: addressCity.trim() || null,
          addressRegion: addressRegion.trim() || null,
          addressPostalCode: addressPostalCode.trim() || null,
          addressCountry: addressCountry.trim() || null,
          rmmName: rmm.trim() || null,
          passwordManagerKind: pmKind || null,
          passwordManagerOtherName: pmKind === 'other' ? pmOther.trim() || null : null,
          startedOn: startedOn || null,
          endedOn: endedOn || null,
          noticeGivenOn: noticeGivenOn || null,
          isActive,
          huduCompanyUrl: huduUrl.trim() || null,
          ticketingSystem: ticketingSystem.trim() || null,
          appSharedMailboxEmail: appMailbox.trim() || null,
        },
      });
      await onSaved();
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  // A client that left block hours is switched off by the daily roll once
  // its end date passes. Project-only keeps it active with block hours off.
  const leftBlockHours = org.endedOn !== null || org.noticeGivenOn !== null;
  const projectOnly = Boolean(org.projectOnlySince);
  const [projectOnlyBusy, setProjectOnlyBusy] = useState(false);
  const [projectOnlyError, setProjectOnlyError] = useState<string | null>(null);

  async function setProjectOnly(on: boolean) {
    const question = on
      ? `Re-enable ${org.name} for projects only?\n\nThe client becomes active again for tickets, projects and quotes. Block hours stay ended, and the end date stays on record for the churn reports.`
      : `Remove project-only from ${org.name}?${
          org.endedOn ? `\n\nIts block hours ended on ${org.endedOn}, so the client becomes inactive.` : ''
        }`;
    if (!window.confirm(question)) return;
    setProjectOnlyBusy(true);
    setProjectOnlyError(null);
    try {
      await apiFetch(`/api/organizations/${org.id}/project-only`, { method: on ? 'POST' : 'DELETE' });
      await onSaved();
    } catch (e) {
      setProjectOnlyError(e instanceof ApiError ? e.message : 'Could not update the client.');
    } finally {
      setProjectOnlyBusy(false);
    }
  }

  const passwordLabel =
    org.passwordManagerKind === 'it_glue'
      ? 'IT Glue Connect'
      : org.passwordManagerKind === 'hudu'
        ? 'Hudu Bridge'
        : org.passwordManagerKind === 'other'
          ? org.passwordManagerOtherName ?? 'Other'
          : null;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[var(--color-header)]">Org profile</h2>
        {!editing && (
          <button
            type="button"
            onClick={() => {
              reset();
              setEditing(true);
            }}
            className="text-sm text-[var(--color-t3-blue)] hover:underline"
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField
              label="Name"
              name="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <FormField
              label="Primary domain"
              name="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              hint="Used to match incoming emails to this org."
            />
            <FormField
              label="Timezone"
              name="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="America/New_York"
              hint="IANA timezone. Controls digest scheduling and date display."
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <FormField
              label="Started on"
              name="startedOn"
              type="date"
              value={startedOn}
              onChange={(e) => setStartedOn(e.target.value)}
              hint="First active month: the month containing this date if it's the 1st, otherwise the next month."
            />
            <FormField
              label="Notice given on"
              name="noticeGivenOn"
              type="date"
              value={noticeGivenOn}
              onChange={(e) => {
                const v = e.target.value;
                setNoticeGivenOn(v);
                // Ended on is automatic: 3 months after the notice date.
                setEndedOn(v ? addMonthsStr(v, NOTICE_PERIOD_MONTHS) : '');
              }}
              hint="Sets Ended on automatically to 3 months later."
            />
            <FormField
              label="Ended on"
              name="endedOn"
              type="date"
              value={endedOn}
              onChange={(e) => setEndedOn(e.target.value)}
              hint="Auto-set to 3 months after notice; editable if needed. First day of NO service."
            />
            <div>
              <Select
                label="Status"
                value={isActive ? 'active' : 'inactive'}
                onChange={(e) => setIsActive(e.target.value === 'active')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
              {endedOn && !projectOnly && (
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Turned off automatically once Ended on passes. To keep a client for project work, use
                  Re-enable for projects only.
                </p>
              )}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Check-in day" value={day} onChange={(e) => setDay(e.target.value as CheckinDay | '')}>
              <option value="">— none —</option>
              {DAYS.map((d) => (
                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
              ))}
            </Select>
            <FormField label="Check-in time (HH:MM, org tz)" name="time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          {/* The COMPANY's own details, as distinct from the main POC
              below (a person). These are what a quote, a signed SOW and
              an invoice are addressed to. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Company phone" name="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <FormField label="Company email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Address line 1" name="addressLine1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
            <FormField label="Address line 2" name="addressLine2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <FormField label="City" name="addressCity" value={addressCity} onChange={(e) => setAddressCity(e.target.value)} />
            <FormField label="State / region" name="addressRegion" value={addressRegion} onChange={(e) => setAddressRegion(e.target.value)} />
            <FormField label="ZIP / postal code" name="addressPostalCode" value={addressPostalCode} onChange={(e) => setAddressPostalCode(e.target.value)} />
            <FormField label="Country" name="addressCountry" value={addressCountry} onChange={(e) => setAddressCountry(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField label="Main POC name" name="pocName" value={pocName} onChange={(e) => setPocName(e.target.value)} />
            <FormField label="Main POC email" name="pocEmail" type="email" value={pocEmail} onChange={(e) => setPocEmail(e.target.value)} />
            <FormField label="Main POC cell" name="pocCell" value={pocCell} onChange={(e) => setPocCell(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="PSA" name="psa" value={psa} onChange={(e) => setPsa(e.target.value)} placeholder="e.g. Autotask, Halo" />
            <FormField label="RMM" name="rmm" value={rmm} onChange={(e) => setRmm(e.target.value)} placeholder="e.g. NinjaOne, Datto" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="Ticketing system"
              name="ticketingSystem"
              value={ticketingSystem}
              onChange={(e) => setTicketingSystem(e.target.value)}
              placeholder="e.g. Synchro, Halo"
            />
            <FormField
              label="App shared mailbox email"
              name="appMailbox"
              type="email"
              value={appMailbox}
              onChange={(e) => setAppMailbox(e.target.value)}
              placeholder="client-prefix@t3techpartners.com"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Password manager" value={pmKind} onChange={(e) => setPmKind(e.target.value as PasswordManagerKind | '')}>
              <option value="">— none —</option>
              <option value="it_glue">IT Glue Connect</option>
              <option value="hudu">Hudu Bridge</option>
              <option value="other">Other</option>
            </Select>
            {pmKind === 'other' && (
              <FormField label="Other password manager name" name="pmOther" value={pmOther} onChange={(e) => setPmOther(e.target.value)} />
            )}
          </div>
          <FormField
            label="Hudu URL (override)"
            name="huduUrl"
            value={huduUrl}
            onChange={(e) => setHuduUrl(e.target.value)}
            placeholder="https://t3techpartners.huducloud.com/c/…"
            hint="Auto-filled when Admin → Hudu match is run. Edit only if Hudu changed the URL or auto-match got it wrong."
          />
          {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
          <div className="flex gap-2">
            <Button onClick={() => void save()} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            <Button variant="outline" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      ) : (
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <Field label="Domain" value={org.domain} />
          <Field label="Timezone" value={org.timezone} />
          <Field
            label="Status"
            value={
              org.isActive
                ? projectOnly
                  ? `Active · projects only since ${org.projectOnlySince}`
                  : 'Active'
                : 'Inactive'
            }
          />
          <Field
            label="Started on"
            value={
              org.startedOn
                ? `${org.startedOn}${org.hoursActive ? ' · hours active' : ' · hours not active'}`
                : null
            }
          />
          <Field label="Notice given" value={org.noticeGivenOn} />
          <Field label="Ended on" value={org.endedOn} />
          <Field label="Check-in" value={
            org.checkinDayOfWeek
              ? `${org.checkinDayOfWeek.charAt(0).toUpperCase() + org.checkinDayOfWeek.slice(1)}${org.checkinTime ? ` @ ${org.checkinTime.slice(0, 5)}` : ''}`
              : null
          } />
          <Field label="PSA" value={org.psaName} />
          <Field label="RMM" value={org.rmmName} />
          <Field label="Ticketing system" value={org.ticketingSystem} />
          <Field label="Main POC" value={org.mainPocName} />
          <Field label="POC email" value={org.mainPocEmail} />
          <Field label="POC cell" value={org.mainPocCell} />
          <Field label="App shared mailbox" value={org.appSharedMailboxEmail} />
          <Field label="Passwords" value={passwordLabel} />
        </dl>
      )}
      {!editing && canManageBilling && leftBlockHours && (
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[var(--color-border)] pt-4">
          {projectOnly ? (
            <>
              <p className="text-sm text-[var(--color-muted)]">
                Block hours have ended. This client stays active for project work.
              </p>
              <Button variant="outline" onClick={() => void setProjectOnly(false)} disabled={projectOnlyBusy}>
                {projectOnlyBusy ? 'Saving…' : 'Remove project-only'}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-[var(--color-muted)]">
                {org.isActive
                  ? 'This client has left block hours and will be switched off when they end.'
                  : 'This client has left block hours.'}
              </p>
              <Button onClick={() => void setProjectOnly(true)} disabled={projectOnlyBusy}>
                {projectOnlyBusy ? 'Saving…' : 'Re-enable for projects only'}
              </Button>
            </>
          )}
          {projectOnlyError && <p className="w-full text-sm text-[var(--color-danger)]">{projectOnlyError}</p>}
        </div>
      )}
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string | null }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</dt>
      <dd>{value ?? <span className="text-[var(--color-muted)]">—</span>}</dd>
    </div>
  );
}
